/**
 * Timer Manager for Non-React Code
 * Provides centralized timer management with automatic cleanup
 * Prevents memory leaks in service and utility files
 */

class TimerManager {
  private timers = new Map<string, NodeJS.Timeout>();
  private intervals = new Map<string, NodeJS.Timeout>();
  private animationFrames = new Map<string, number>();
  private cleanupHandlers = new Set<() => void>();
  private isCleaningUp = false;

  constructor() {
    // Register global cleanup handlers
    if (typeof window !== 'undefined') {
      const cleanup = () => this.cleanup();
      window.addEventListener('beforeunload', cleanup);
      window.addEventListener('pagehide', cleanup);
      
      // Store cleanup handler for removal
      this.cleanupHandlers.add(() => {
        window.removeEventListener('beforeunload', cleanup);
        window.removeEventListener('pagehide', cleanup);
      });
    }

    // Track instance for debugging
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      (window as any).__timerManager = this;
    }
  }

  /**
   * Set a timeout with automatic cleanup
   */
  setTimeout(
    callback: () => void,
    delay: number,
    key?: string
  ): string {
    const id = key || `timeout_${Date.now()}_${Math.random()}`;
    
    // Clear existing timer with same key
    this.clearTimeout(id);
    
    const timeoutId = setTimeout(() => {
      this.timers.delete(id);
      if (!this.isCleaningUp) {
        callback();
      }
    }, delay);
    
    this.timers.set(id, timeoutId);
    return id;
  }

  /**
   * Set an interval with automatic cleanup
   */
  setInterval(
    callback: () => void,
    delay: number,
    key?: string
  ): string {
    const id = key || `interval_${Date.now()}_${Math.random()}`;
    
    // Clear existing interval with same key
    this.clearInterval(id);
    
    const intervalId = setInterval(() => {
      if (!this.isCleaningUp) {
        callback();
      }
    }, delay);
    
    this.intervals.set(id, intervalId);
    return id;
  }

  /**
   * Request animation frame with automatic cleanup
   */
  requestAnimationFrame(
    callback: (time: number) => void,
    key?: string
  ): string {
    const id = key || `raf_${Date.now()}_${Math.random()}`;
    
    // Clear existing animation frame with same key
    this.cancelAnimationFrame(id);
    
    const rafId = requestAnimationFrame((time) => {
      this.animationFrames.delete(id);
      if (!this.isCleaningUp) {
        callback(time);
      }
    });
    
    this.animationFrames.set(id, rafId);
    return id;
  }

  /**
   * Clear a specific timeout
   */
  clearTimeout(id: string): void {
    const timeoutId = this.timers.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timers.delete(id);
    }
  }

  /**
   * Clear a specific interval
   */
  clearInterval(id: string): void {
    const intervalId = this.intervals.get(id);
    if (intervalId) {
      clearInterval(intervalId);
      this.intervals.delete(id);
    }
  }

  /**
   * Cancel a specific animation frame
   */
  cancelAnimationFrame(id: string): void {
    const rafId = this.animationFrames.get(id);
    if (rafId) {
      cancelAnimationFrame(rafId);
      this.animationFrames.delete(id);
    }
  }

  /**
   * Clear all timers of a specific type
   */
  clearAllTimeouts(): void {
    this.timers.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timers.clear();
  }

  clearAllIntervals(): void {
    this.intervals.forEach((intervalId) => clearInterval(intervalId));
    this.intervals.clear();
  }

  clearAllAnimationFrames(): void {
    this.animationFrames.forEach((rafId) => cancelAnimationFrame(rafId));
    this.animationFrames.clear();
  }

  /**
   * Cleanup all timers and handlers
   */
  cleanup(): void {
    this.isCleaningUp = true;
    
    // Clear all timers
    this.clearAllTimeouts();
    this.clearAllIntervals();
    this.clearAllAnimationFrames();
    
    // Remove event listeners
    this.cleanupHandlers.forEach(handler => handler());
    this.cleanupHandlers.clear();
    
    this.isCleaningUp = false;
  }

  /**
   * Get timer statistics for debugging
   */
  getStats(): {
    timeouts: number;
    intervals: number;
    animationFrames: number;
    total: number;
  } {
    return {
      timeouts: this.timers.size,
      intervals: this.intervals.size,
      animationFrames: this.animationFrames.size,
      total: this.timers.size + this.intervals.size + this.animationFrames.size
    };
  }

  /**
   * Create a scoped timer manager for a specific component/service
   */
  createScope(scopeName: string): ScopedTimerManager {
    return new ScopedTimerManager(this, scopeName);
  }
}

/**
 * Scoped timer manager for isolated timer management
 */
class ScopedTimerManager {
  private scopedTimers = new Set<string>();
  
  constructor(
    private manager: TimerManager,
    private scopeName: string
  ) {}

  setTimeout(callback: () => void, delay: number): string {
    const id = this.manager.setTimeout(callback, delay, `${this.scopeName}_timeout_${Date.now()}`);
    this.scopedTimers.add(id);
    return id;
  }

  setInterval(callback: () => void, delay: number): string {
    const id = this.manager.setInterval(callback, delay, `${this.scopeName}_interval_${Date.now()}`);
    this.scopedTimers.add(id);
    return id;
  }

  requestAnimationFrame(callback: (time: number) => void): string {
    const id = this.manager.requestAnimationFrame(callback, `${this.scopeName}_raf_${Date.now()}`);
    this.scopedTimers.add(id);
    return id;
  }

  clearTimeout(id: string): void {
    this.manager.clearTimeout(id);
    this.scopedTimers.delete(id);
  }

  clearInterval(id: string): void {
    this.manager.clearInterval(id);
    this.scopedTimers.delete(id);
  }

  cancelAnimationFrame(id: string): void {
    this.manager.cancelAnimationFrame(id);
    this.scopedTimers.delete(id);
  }

  cleanup(): void {
    this.scopedTimers.forEach(id => {
      this.manager.clearTimeout(id);
      this.manager.clearInterval(id);
      this.manager.cancelAnimationFrame(id);
    });
    this.scopedTimers.clear();
  }
}

// Create singleton instance
export const timerManager = new TimerManager();

// Export convenience functions
export const managedSetTimeout = timerManager.setTimeout.bind(timerManager);
export const managedSetInterval = timerManager.setInterval.bind(timerManager);
export const managedRequestAnimationFrame = timerManager.requestAnimationFrame.bind(timerManager);
export const clearManagedTimeout = timerManager.clearTimeout.bind(timerManager);
export const clearManagedInterval = timerManager.clearInterval.bind(timerManager);
export const cancelManagedAnimationFrame = timerManager.cancelAnimationFrame.bind(timerManager);

// Export the classes for advanced usage
export { TimerManager, ScopedTimerManager };

// Helper function for promise-based delays
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => {
    timerManager.setTimeout(resolve, ms);
  });
}

// Helper for retrying with exponential backoff
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: {
    maxAttempts?: number;
    initialDelay?: number;
    maxDelay?: number;
    backoffFactor?: number;
    onRetry?: (attempt: number, error: Error) => void;
  } = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    initialDelay = 1000,
    maxDelay = 30000,
    backoffFactor = 2,
    onRetry
  } = options;

  let currentDelay = initialDelay;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxAttempts) {
        throw error;
      }
      
      onRetry?.(attempt, error as Error);
      
      await delay(currentDelay);
      currentDelay = Math.min(currentDelay * backoffFactor, maxDelay);
    }
  }
  
  throw new Error('Retry failed');
}